import { data } from 'autoprefixer';
import React from 'react';
import { Link } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import {
  createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable
} from '@tanstack/react-table';

import CenterLoadingSpinner from '../../../components/LoadingSpinner/CenterLoadingSpinner';
import { useAccountContext } from '../../../contexts/AccountContext';
import { humanizedTimeSpan, toLocalTime } from '../../../libs/libDate';
import { sortBy } from '../../../libs/utils';
import { ScimIntegration, useSCIM_IntegrationsApi } from '../../../middle_auth_api';

const columnHelper = createColumnHelper<ScimIntegration>();

const columns = [
  columnHelper.accessor('name', {
    header: () => 'Name',
    cell: info => <Link to={info.row.original.id} className={`underline ${info.row.original.enabled ? '' : 'line-through'}`}>{info.getValue()}</Link>
  }),
  columnHelper.accessor('idp_hint', {
    header: () => 'IdP',
    cell: info => info.row.original.idp_hint == 'other' ? `Other: ${info.row.original.idp_other_name}` : info.getValue()
  }),
  columnHelper.accessor('tokens', {
    header: () => 'Last Used',
    cell: info => {
      const sortedTokens = info.row.original.tokens.sort(sortBy('last_use_timestamp'));
      if (sortedTokens.length == 0 || sortedTokens[0].last_use_timestamp == null) {
        return <i>Never</i>
      } else {
        return <span title={toLocalTime(sortedTokens[0].last_use_timestamp as string).toLocaleString()}>{humanizedTimeSpan(toLocalTime(sortedTokens[0].last_use_timestamp as string))}</span>;
      }
    }
  })
]

const ScimIntegrationsList: React.FC = () => {
  const accountContext = useAccountContext();
  const scimIntegrationsApi = useSCIM_IntegrationsApi();

  const integrationsQuery = useQuery(['scim-integrations', accountContext.currentAccount?.id], async () => {
    const integrations = await scimIntegrationsApi.list_scim_integrations();

    return integrations;
  });

  const table = useReactTable({
    data: integrationsQuery.data?.results.sort(sortBy('name')) ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  if (integrationsQuery.data == null) {
    return <CenterLoadingSpinner />
  }


  return <>
      <table className='w-full p-3'>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className='border-b border-b-background3-dark'>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className='py-3 text-left px-2'>
                  {header.isPlaceholder
                    ? null
                    : <div>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className='border-b border-b-background3-dark hover:bg-hover'>
              {row.getVisibleCells().map(cell => {
                return (
                  <td key={cell.id} className='p-2 whitespace-nowrap overflow-ellipsis'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
          {integrationsQuery.data.count == 0 && <>
            <tr className='border-b border-b-background3-dark'>
              <td colSpan={2} className='p-2 whitespace-nowrap overflow-ellipsis text-center'>
                No SCIM Integrations configured for this Organization
              </td>
            </tr>
          </>}
        </tbody>
      </table>
      <div className='mt-4 mb-2'>
        <Link to='new' className='ml-1 mt-3 px-2 py-2 rounded-lg text-success-text bg-success hover:bg-success-dark shadow-md shadow-hover'>New</Link>
      </div>
  </>;
};

export default ScimIntegrationsList